// The seventeen event types and their payloads.
//
// Transcribed from 01-event-schema.md section 5 in its DRAFT state. O-059 is
// open, so this file is the first thing that changes if the schema moves under
// review. It is kept deliberately flat and declarative so that a change is a
// change to a literal rather than to logic.
//
// The list is closed. If a proposed format needs an eighteenth type, the test
// from the brief applies: that is a signal the spine is wrong, not that the
// format is special.
//
// attestation_recorded was the seventeenth, added 2026-08-26, and it is worth
// recording why it passed a test that voucher redemption failed. The test is
// whether the new thing is the same fact wearing different clothes. Voucher
// redemption is a scan, so it became a purpose on touchpoint_scanned. An
// attestation is not a consent.
//
// A consent is permission to process personal data, and under UK GDPR it is
// only a consent at all if it was freely given, which means it can be refused
// and it can be withdrawn. An attestation is a statement of fact about the
// person ("I am 18 or older") or an act of agreement to a document ("I agree
// to the Official Rules"). It may be required, it cannot be withdrawn in the
// sense a consent can, and refusing it does not mean processing stops, it
// means there is no entry.
//
// So carrying an attestation as a consent_captured row with some new purpose
// would put a required act into the one table whose meaning is "freely given".
// Every consent count, every consent export, every Send gate and every
// withdrawal path reads that table. An age attestation landing there is
// countable as a consent, withdrawable as a consent, and arguable in exactly
// the way Article 7(4) makes things arguable. Two different facts, two types.

import * as v from "./validators.js";

export const EVENT_TYPES = [
  "registration_started",
  "registration_completed",
  "consent_captured",
  "consent_withdrawn",
  "attestation_recorded",
  "checkin_recorded",
  "prize_entry_recorded",
  "participation_started",
  "participation_completed",
  "score_submitted",
  "touchpoint_scanned",
  "asset_created",
  "asset_delivery_requested",
  "asset_delivered",
  "export_generated",
  "station_health_reported",
  "event_voided",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

// ---------------------------------------------------------------- controlled lists

/**
 * Distinct purposes rather than one blanket consent.
 *
 * image_capture and gallery_display are separate because Send depends on them
 * separately: a photograph may be taken and delivered to the attendee without
 * ever being displayable on a gallery screen, and collapsing the two would make
 * that impossible to express.
 */
export const CONSENT_PURPOSES = [
  "contact",
  "image_capture",
  "gallery_display",
  "client_data_transfer",
  "marketing",
  "marketing_email",
  "marketing_sms",
  "marketing_phone",
  "enquiry_follow_up",
  "image_marketing",
  "profiling",
] as const;

/**
 * The two kinds of act an attestation can be, and they are not interchangeable.
 *
 * eligibility is a statement of fact about the person: "I am 18 or older", "I
 * am a UK resident". Nobody is being agreed with. It is the attendee asserting
 * something about themselves, and its evidential value is entirely in the fact
 * that they performed an act to assert it.
 *
 * terms_acceptance is agreement to one or more documents: the Official Rules, a
 * set of terms, a privacy notice acknowledged as read. There is a counterparty,
 * and 13-legal-surfaces section 3 requires that the counterparty is named.
 *
 * They are kept apart because bundling them is the failure. An interface that
 * offers "I am 18 and I agree to the Official Rules" as one tick has collected
 * one act and claims two facts, and the age half is the half that gets argued
 * about when a prize is disputed. Migration 009 refuses an eligibility
 * attestation carrying documents, so the separation is a database fact rather
 * than a convention the next interface can quietly drop.
 */
export const ATTESTATION_KINDS = ["eligibility", "terms_acceptance"] as const;

/**
 * What a document is, which decides where it may appear rather than styling it.
 *
 * official_rules is called out because the Gambling Act 2005 position and
 * O-056 both turn on a prize draw having published rules that were reachable at
 * the moment of entry. cookie_notice is the client's cookie policy when they
 * have written one; the phone still generates a storage notice from what it
 * actually stores if this kind is not in the bundle. photo_release is the
 * likeness grant a marketing-image tick links; sms_terms is the SMS document
 * a US text-marketing tick links. Neither is a registration tick on its own.
 */
export const DOCUMENT_KINDS = [
  "terms",
  "privacy_notice",
  "cookie_notice",
  "official_rules",
  "photo_release",
  "sms_terms",
] as const;

export const VOID_REASONS = [
  "duplicate_capture",
  "test_data",
  "cheated_score",
  "offensive_display_name",
  "wrong_attendee",
  "consent_error",
  "operator_error",
  "technical_fault",
  "withdrawn_by_attendee",
] as const;

/** Where an interaction physically happened. */
export const SURFACES = ["station", "fixture", "attendee_device"] as const;

/**
 * How well the person's identity was established, at the moment of capture.
 *
 * Two records that look identical in an export are not: a crew member typing an
 * address a stranger read out has produced a string that nobody has ever
 * delivered anything to, and a self-serve web registration has produced an
 * address whose owner opened a message sent to it. A client segmenting for
 * follow-up needs to be able to tell those apart, and so does anyone answering
 * a complaint about who consented to what.
 *
 * MANDATORY IN EVERY EXPORT, with these definitions carried alongside. A closed
 * enum whose values are only meaningful next to their definitions is a column of
 * jargon in a spreadsheet, and the definitions rot the moment they live anywhere
 * but here.
 *
 * STORED THOUGH DERIVABLE. Today this is a function of capture_channel and
 * nothing else, so it could be computed on read. Derivable-today becomes
 * wrong-tomorrow the first time somebody builds a crew-assisted web flow — a
 * tablet handed over in a queue that still sends a code — and at that point
 * every historical row recomputed by the new rule silently changes meaning. The
 * fact is recorded when it is known and never recalculated.
 */
export const IDENTITY_ASSURANCE = [
  /** Somebody typed it. Nothing has been sent to it and nothing has come back. */
  "self_asserted",
  /** A code or link sent to the address was returned, so somebody could read that mailbox. */
  "mailbox_verified",
] as const;

const surface = () => v.oneOf(SURFACES);

// ---------------------------------------------------------------------- payloads

const registrationStarted = v.object({
  surface: surface(),
  field_set_version: v.integer(),
  entry_point: v.oneOf(["qr", "direct", "crew_initiated", "attract"]),
  resumed: v.boolean(),
});

const registrationCompleted = v.object({
  // Null is allowed because a crew-assisted registration can begin at the
  // completed step. A registration with a start and no completion is an
  // abandonment, and no separate abandonment event exists or is needed.
  started_event_id: v.nullable(v.uuid()),
  surface: surface(),
  capture_mode: v.oneOf(["self_serve", "assisted"]),
  assisted_by: v.nullable(v.string()),
  field_set_version: v.integer(),

  // Typed slots rather than configured fields, and the reasoning matters: if
  // the delivery address were only a configured field then Send would have to
  // know the field set of every activation in order to route anything.
  //
  // contact_email is the attendee's own. deliver_to_email is a routing
  // override, for a guest's photograph going to the inviting attendee.
  //
  // Every slot is nullable. No placeholder email is ever generated, and an
  // attendee with no email is a valid record whose delivery falls back to a QR
  // on their own device.
  contact: v.object({
    display_name: v.nullable(v.string()),
    contact_email: v.nullable(v.string()),
    deliver_to_email: v.nullable(v.string()),
    phone: v.nullable(v.string()),
  }),

  // Validated against the field-type library at field_set_version, which is
  // deliverable 6 and not yet written. Until it is, the shape is checked and
  // the contents are not.
  fields: v.record(),
  duration_ms: v.nullable(v.integer()),

  // WHEN THE ADDRESS WAS PROVED, OR NULL BECAUSE IT WAS NOT.
  //
  // A nullable timestamp and deliberately not a boolean. `verified: false`
  // written by the tablet path and `verified: false` written by a web
  // registration that skipped verification would be the same value standing for
  // two different situations, which is how a column starts meaning whatever the
  // code path that wrote it meant. A timestamp cannot do that: it is either a
  // moment or it is the absence of one.
  //
  // It also carries the answer to *when*, which is the first question asked in a
  // consent challenge and the one a boolean can never answer. The address was
  // proved eleven months ago is a different fact from the address was proved
  // during this registration, and only one of them supports a claim about what
  // the person understood at the time.
  //
  // Null on every tablet registration today. That is not a gap: nothing was sent
  // to the address and nothing came back, so there is no moment to record.
  email_verified_at: v.nullable(v.timestamp()),

  // See IDENTITY_ASSURANCE for why this is stored even though it is currently a
  // function of the channel, and for the definitions that have to travel with it
  // into an export.
  identity_assurance: v.oneOf(IDENTITY_ASSURANCE),
});

const consentCaptured = v.object({
  consent_version_id: v.string(),
  // sha-256 of the exact text shown. The full text of each version is stored
  // once, centrally, and is exportable, so an export can prove what a person
  // agreed to without carrying the paragraph on every row.
  //
  // MARKED FOR LEGAL REVIEW under O-057: whether a hash plus centrally stored
  // text satisfies the evidential standard, or whether the full wording must
  // ride on the event. If the answer is the second one, this shape changes.
  wording_hash: v.string(),
  purpose: v.oneOf(CONSENT_PURPOSES),
  // Only ever true. A refusal is the absence of a consent event, not a consent
  // event carrying false, because the two are different facts and only one of
  // them is evidence of anything.
  granted: v.literal(true),
  method: v.oneOf(["tap", "signature", "verbal_recorded_by_crew"]),
  surface: surface(),
});

const consentWithdrawn = v.object({
  consent_version_id: v.string(),
  purpose: v.oneOf(CONSENT_PURPOSES),
  method: v.oneOf(["attendee_request", "crew_action", "data_request"]),
  captured_offline: v.boolean(),
  note: v.nullable(v.string()),
});

const attestationRecorded = v.object({
  attestation_version_id: v.string(),

  // sha-256 of the exact text shown, on the same terms as consent_captured.
  // Migration 009 holds an append-only attestation_wording table keyed on this
  // hash and refuses any attestation citing a hash that table cannot produce.
  //
  // The reason attestations get the same discipline as consents, rather than
  // being trusted because they are shorter, is that the disputed question is
  // never "did they tick something". It is "what did the rules say at that
  // moment". A prize draw challenged eight months later is a question about a
  // paragraph, and a paragraph nobody kept a copy of is not an answer.
  wording_hash: v.string(),

  kind: v.oneOf(ATTESTATION_KINDS),

  // Which documents this act covers. Empty on an eligibility attestation, and
  // migration 009 enforces that rather than trusting the writer: a statement
  // about oneself agrees to nothing.
  //
  // A list rather than one document because the rules and the terms may share
  // a control. Agreeing to two documents is one kind of act. The version is
  // carried per document so that an export can say which revision was in force
  // without joining through the bundle that happened to be deployed.
  documents: v.array(
    v.object({
      document_id: v.string(),
      version: v.string(),
      kind: v.oneOf(DOCUMENT_KINDS),
    }),
  ),

  // Whose terms these are, by legal name. Null on an eligibility attestation,
  // because a statement about oneself is made to nobody in particular.
  //
  // At an activation the client is usually the controller and Nova the
  // processor, so "our terms" is usually theirs. The interface must not assume
  // and neither may a report, which is why the name is on the event and not
  // inferred from whichever party happened to own the station.
  party: v.nullable(v.string()),

  // Only ever true, for the same reason granted is on consent_captured. A
  // refusal is the absence of an attestation, not an attestation carrying
  // false. Someone who did not tick "I am 18 or older" has not asserted that
  // they are under 18, they have asserted nothing, and writing a row that
  // implies otherwise invents a fact about a person.
  confirmed: v.literal(true),

  method: v.oneOf(["tap", "signature", "verbal_recorded_by_crew"]),
  surface: surface(),
});

const checkinRecorded = v.object({
  method: v.oneOf(["personal_qr", "manual_lookup", "nfc", "crew_override"]),
  // Increments per visit rather than the record being updated. Rule 1.
  visit_index: v.integer(),
  matched_on: v.oneOf(["attendee_ref", "contact_email", "display_name"]),
});

const prizeEntryRecorded = v.object({
  // On the event because the Gambling Act 2005 position depends on which route
  // an entry took, and an audit that cannot say which is not an audit.
  // Governed by O-056, and no prize mechanic runs before that review.
  route: v.oneOf(["free_entry", "skill"]),
  draw_pool_id: v.string(),
  qualifying_event_id: v.nullable(v.uuid()),
  entry_index: v.integer(),
});

const participationStarted = v.object({
  activity_id: v.string(),
  activity_version: v.nullable(v.string()),
  surface: v.oneOf([...SURFACES, "big_screen"]),
});

const participationCompleted = v.object({
  started_event_id: v.uuid(),
  // Two ways to end without completing, and both are needed. An explicit
  // abandoned covers someone quitting a game. An unclosed pair covers someone
  // who orders a coffee and walks out. expired is written by the activation
  // close routine against pairs still open at the end, and is the one place the
  // server originates a spine event rather than receiving one.
  outcome: v.oneOf(["completed", "abandoned", "expired"]),
  duration_ms: v.nullable(v.integer()),
});

const scoreSubmitted = v.object({
  participation_event_id: v.uuid(),
  template_id: v.string(),
  template_version: v.string(),
  score: v.number(),
  score_unit: v.oneOf(["points", "milliseconds", "count"]),
  duration_ms: v.integer(),
  display_name: v.nullable(v.string()),
  display_name_status: v.oneOf(["accepted", "filtered", "pending_review"]),
  // A flagged score is written, not suppressed, and excluded from published
  // boards until a crew member reviews it. A rejected score is also written,
  // because an attempt to cheat is itself a fact worth having and dropping it
  // destroys the evidence.
  validation: v.object({
    status: v.oneOf(["accepted", "flagged", "rejected"]),
    rules_version: v.string(),
    failed_rules: v.array(v.string()),
  }),
});

const touchpointScanned = v.object({
  // Carries decision B: a purpose field rather than a seventeenth type for
  // voucher redemption. The type covers core scans as well as the sensor
  // add-on, which is what the recommendation buys.
  purpose: v.oneOf(["checkin", "pickup", "redemption", "zone", "other"]),
  touchpoint_id: v.string(),
  reader_type: v.oneOf(["qr", "nfc", "rfid"]),
  // The adapter, never the hardware, so a reader change is configuration and
  // not a schema change. Constraint 7, and no vendor is ever named in copy.
  reader_adapter: v.string(),
  scanned_ref: v.nullable(v.string()),
});

const stationHealthReported = v.object({
  queue_depth: v.integer(),
  oldest_unsent_at: v.nullable(v.timestamp()),
  last_sync_at: v.nullable(v.timestamp()),
  battery_percent: v.nullable(v.integer()),
  charging: v.nullable(v.boolean()),
  storage_free_bytes: v.integer(),
  storage_total_bytes: v.integer(),
  connectivity: v.oneOf(["none", "metered", "wifi", "ethernet"]),
  app_version: v.string(),
  // Lets the server detect a stale device before it sends something the server
  // cannot read.
  schema_version_supported: v.integer(),
});

const assetCreated = v.object({
  asset_id: v.uuid(),
  // A voucher is an asset. A discount code or reward QR is created and
  // delivered through the same three events as a photograph, which is why the
  // games capability needs no new type.
  kind: v.oneOf(["photo", "video", "file", "voucher"]),
  content_hash: v.string(),
  byte_size: v.integer(),
  mime_type: v.string(),
  capture_source: v.string(),
  // The consents in force at the moment of capture. Send reads this and never
  // assumes.
  consent_version_ids: v.array(v.string()),
  storage_state: v.oneOf(["local_only", "uploaded"]),
});

const assetDeliveryRequested = v.object({
  asset_id: v.uuid(),
  channel: v.oneOf(["qr", "link", "email", "sms"]),
  // Names which of the two email slots to use and never carries the address.
  // The address lives on the registration, and repeating it here would
  // duplicate personal data across the spine and make erasure harder for no
  // gain.
  destination_type: v.oneOf(["contact", "deliver_to"]),
  requested_by: v.oneOf(["attendee", "crew", "scheduled"]),
});

const assetDelivered = v.object({
  asset_id: v.uuid(),
  delivery_request_event_id: v.uuid(),
  channel: v.oneOf(["qr", "link", "email", "sms"]),
  // suppressed is the outcome where delivery was requested but consent had been
  // withdrawn by the time it was attempted. It exists because Send gates on
  // consent at delivery time on the server rather than at capture time on the
  // device, which is the only place the offline withdrawal race can be won.
  outcome: v.oneOf(["delivered", "bounced", "failed", "suppressed"]),
  provider_adapter: v.string(),
  provider_ref: v.nullable(v.string()),
  failure_reason: v.nullable(v.string()),
});

const exportGenerated = v.object({
  export_template_id: v.string(),
  template_version: v.string(),
  scope: v.object({
    activation_ids: v.array(v.uuid()),
    report_id: v.string(),
    filters_applied: v.array(v.string()),
  }),
  format: v.oneOf(["csv", "xlsx", "json"]),
  row_count: v.integer(),
  column_set: v.array(v.string()),
  actor_id: v.string(),
  actor_role: v.oneOf(["nova_admin", "client_viewer"]),
  destination: v.oneOf(["download", "scheduled_email"]),
});

const eventVoided = v.object({
  voided_event_id: v.uuid(),
  voided_event_type: v.oneOf(EVENT_TYPES),
  reason_code: v.oneOf(VOID_REASONS),
  note: v.nullable(v.string()),
  actor_id: v.string(),
  actor_role: v.oneOf(["nova_admin", "crew"]),
});

/** Payload validator per type. Every type in EVENT_TYPES has exactly one. */
export const PAYLOADS = {
  registration_started: registrationStarted,
  registration_completed: registrationCompleted,
  consent_captured: consentCaptured,
  consent_withdrawn: consentWithdrawn,
  attestation_recorded: attestationRecorded,
  checkin_recorded: checkinRecorded,
  prize_entry_recorded: prizeEntryRecorded,
  participation_started: participationStarted,
  participation_completed: participationCompleted,
  score_submitted: scoreSubmitted,
  touchpoint_scanned: touchpointScanned,
  asset_created: assetCreated,
  asset_delivery_requested: assetDeliveryRequested,
  asset_delivered: assetDelivered,
  export_generated: exportGenerated,
  station_health_reported: stationHealthReported,
  event_voided: eventVoided,
} as const satisfies Record<EventType, unknown>;
